import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchWithResponse } from 'src/Types/api'
import { getMyPvPOSTDto, MyPvPUTDto } from '../../parsers'
import { HeatpumpIo, Htc320hIo, MyPvIo } from '../forms'
import { DeviceStatus, LastKnownDeviceStatus, PairingState } from './installations'
import { sanitizeToLatin } from '../utils'

/** Common properties for all devices */
export type DeviceBaseDto = {
  id: number
  deviceLinkId: number
  deviceName: string
  pairingState: PairingState
  deviceStatus: DeviceStatus
  index: number
  lastKnownDeviceStatus: LastKnownDeviceStatus
  lastKnownDeviceStatusTimestamp: string

  /**
   * Bits from embedded representing the devices errors and/or warning.
   * 0 is no error/warnings.
   */
  deviceStatusCode: number | null
}

/** CONTROLLED */
type ControlledDtoBase = DeviceBaseDto & {
  type: 'Evse'
  evseSubTypeParameters: {
    wallboxId: string | null
    /**
     * - 6 or null: "Cintech" (default)
     * - 7: "EEbus" (Hager | Audi)
     * - 8: "WittyLink"
     * - 11: "WittyFlex" (EDSN)
     */
    wbType: '6' | '7' | '8' | '11' | null

    /**
     * The exact reference of the device.
     * TODO: Fill the list of values in this comment.
     */
    deviceReference: string | null

    /**
     * Only relevant for wbType '6' ("Cintech"): distinguishes the "Multi Connect" hardware variants.
     * - 0 or 1: "Multi Connect"
     * - 2: "Multi Connect 2"
     */
    keyLockMode: number | null

    /**
     * Hager or Audi
     */
    brand: string | null

    /* OCPP parameters, applicable only for wbType: 8 ("WittyLink") */
    ocppId: string | null
    ocppAuthType: 'No' | 'basic' | null
    ocppAllowLocalCharge: boolean | null
    ocppServerAddress: string | null
    ocppActivation: boolean | null
  }

  parameters: Nullable<{
    activated: boolean
    authenticationMode: 'freeAccess' | 'key' | 'badge'
    chargeInFallbackModeAllowed: boolean
    fallbackMaxCurrent: number
    ledIntensity: number
    lockCable: boolean
    minCurrent: number
    minCurrentSunMode: number
    /** Present only for ModbusTcp. Excluded from EEBus GET, POST and PUT requests. */
    phaseMapping?: '123' | '132' | '231' | '213' | '312' | '321'
    phasesManagement: 'AutoPhaseSwitch' | '1PhBlocked' | '3PhBlocked'
    priority: number
    prioritySum: number
    protection: number
    pulseWeight: number
    useMidMeter: boolean
    sunMode: Nullable<{
      activated: boolean
      chargingStrategy: 'Delayed' | 'Immediate' | 'Disabled'
      /**
       * Entry index is the week day. 0 - Monday, 1 - Tuesday...
       * Array with exactly 7 entries, one for each day of the week.
       */
      parameterList: {
        /** Time in seconds from start of day (as string) */
        daytime?: string
        /** Number (as string) */
        minEnergy: string
      }[]
      holdingTimeInMin: number
    }>
  }>
}

type ModbusTcpControlledDto = {
  media: 'ModbusTCP'
  mediaParameters: {
    localId: string | null
    macAddress: string | null
    serialNumber: string | null
  }
}

type EebusControlledDto = {
  media: 'EEBus'
  mediaParameters: {
    localId: string | null
    ski: string | null
    serialNumber: number | null
  }
}

export type ControlledDto = ControlledDtoBase & (ModbusTcpControlledDto | EebusControlledDto)

/** METER */
type MeterDtoBase = DeviceBaseDto & {
  media: 'ModbusRTU'
  mediaParameters: {
    address: number | null
    serialNumber?: string | null
    communicationInterfaceAddress?: string | null
  }
  wiringMode: 'Normal' | 'Inverted'
}

type HvacMeterDto = {
  deviceType: 'HVAC'
  typeParameters: { nominalPower: number | null }
}
type PvExternMeterDto = {
  deviceType: 'PVExtern'
  typeParameters: {
    peakPower: number | null
  }
}
type HouseMeterDto = { deviceType: 'House'; typeParameters: Record<string, never> /* {} */ }
type EvseMeterDto = {
  deviceType: 'Evse'
  typeParameters: {
    wallboxId: null
  }
}

export type MeterDto = MeterDtoBase & (HvacMeterDto | HouseMeterDto | PvExternMeterDto | EvseMeterDto)

/** CONTROLLER */
type ModbusRtuControllerDto = DeviceBaseDto & {
  type: 'IOController'
  media: 'ModbusRTU'
  mediaParameters: {
    address: number | null
  }
  parameters: {
    controlMode: null
    boostConfig: []
    inputReadFreq: 2
    nominalPower: null
  }
  outputConfiguration: 'SGReady'
  outputs: [
    {
      id: number
      deviceType: 'HVAC'
      typeParameters: {
        nominalPower: number
      }
      features: 'SGReady1'
    },
    {
      id: number
      deviceType: 'HVAC'
      typeParameters: {
        nominalPower: number
      }
      features: 'SGReady2'
    }
  ]
  inputs: [
    {
      type: 'consumption'
      status: DeviceStatus
      active: true
      index: 0
      value: number
    },
    {
      type: 'production'
      status: DeviceStatus
      active: true
      index: 1
      value: 0
    }
  ]
}

export type StorageControllerDto = DeviceBaseDto & {
  type: 'IOController'
  media: 'Storage'
  mediaParameters: {
    deviceLinkId: number | null
    deviceId?: string | null
    deviceShortUuid?: string | null
    deviceUuid?: string | null
  }
  parameters: {
    controlMode: null
    boostConfig: []
    inputReadFreq: 2
    nominalPower: null
  }
  outputConfiguration: 'SGReady'
  outputs: [
    {
      id: number
      deviceType: 'HVAC'
      typeParameters: {
        nominalPower: number
      }
      features: 'SGReady1'
    },
    {
      id: number
      deviceType: 'HVAC'
      typeParameters: {
        nominalPower: number
      }
      features: 'SGReady2'
    }
  ]
  inputs: []
}

export type MyPvControllerDto = DeviceBaseDto & {
  type: 'MyPvHeater'
  media: 'ModbusTCP'
  mediaParameters: {
    localId: null
    macAddress: null
    /** MyPV serial number */
    serialNumber: string
  }
  parameters: {
    inputReadFreq: 2
    nominalPower: null
    /**
     * - All - "Boost"
     * - Surplus - "Solar only"
     * - Solar - "Solar only with priority"
     * - No - "Security temperature"
     * - Battery - unused
     * - Derated - unused
     */
    controlMode: 'All' | 'Surplus' | 'Solar' | 'No' | 'Battery' | 'Derated'

    /**
     * 0 to 4 entries.
     *
     * Entries must not overlap in time range.
     */
    boostConfig: {
      /**
       * Days of the week (Monday first) as 0 or 1, depending if they are activated.
       * Examples:
       * - "0;0;0;0;0;1;1" Saturday and Sunday on
       * - "1;0;0;0;1;0;0" Monday and Friday on
       */
      day: string
      /** H:mm */
      from: string
      /** H:mm */
      to: string
      active: boolean
      minTempC: number
    }[]
  }
  outputConfiguration: null
  outputs: []
  inputs: []
}

type HeatpumpControllerDto = DeviceBaseDto & {
  type: 'HpWithISG'
  media: 'ModbusTCP'
  mediaParameters: {
    localId: null
    /**
     * Pairing id as well as serial number of heatpump controller
     *
     * Example: 00:11:22:AA:EE:FF
     */
    macAddress: string
    serialNumber: null
  }
  parameters: {
    controlMode: null
    boostConfig: []
    inputReadFreq: 2
    nominalPower: number
  }
  outputConfiguration: null
  outputs: []
  inputs: []
}

export type ControllerDto = ModbusRtuControllerDto | StorageControllerDto | MyPvControllerDto | HeatpumpControllerDto

export interface SubDevicesDto {
  controlled: Nullable<ControlledDto>[]
  monitoring: {
    meters: Nullable<MeterDto>[]
  }
  controllers: Nullable<ControllerDto>[]
}

export const getSubDevices = async (
  installationId: number,
  emcId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<SubDevicesDto | null>> => {
  try {
    const response = await authFetch<SubDevicesDto>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcId}/sub`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<SubDevicesDto>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get sub devices:')
  }
}

/**
 * Fetches only the `controlled` sub-devices (e.g. wallboxes) of a device-link, as opposed to
 * {@link getSubDevices} which fetches monitoring meters and controllers too. Useful to avoid being
 * affected by unrelated errors in other sub-device types (e.g. legacy power meters failing the
 * backend's response validation for `monitoring.meters[].media`).
 *
 * The response is in "array shape" (e.g. `{ id: [1, 2], deviceName: ['a', 'b'] }`), see {@link transposeArrayType}.
 */
export const getControlledSubDevices = async (
  installationId: number,
  emcId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ArrayType<Nullable<ControlledDto>> | null>> => {
  try {
    const response = await authFetch<ArrayType<Nullable<ControlledDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcId}/sub/controlled`,
    })

    /** The route responds 204 (no body) when the device-link doesn't have any controlled sub-devices. */
    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<ArrayType<Nullable<ControlledDto>>>(null)
    }

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null, true)
    }

    return new ApiResult<ArrayType<Nullable<ControlledDto>>>(response.data)
  } catch (error) {
    return handleFetchError<ArrayType<Nullable<ControlledDto>>>(error)
  }
}

export type ControlledPOSTDto = Pick<ControlledDto, 'deviceName' | 'media' | 'type'> & {
  parameters: Pick<
    ControlledDto['parameters'],
    | 'priority'
    | 'authenticationMode'
    | 'phasesManagement'
    | 'activated'
    | 'minCurrent'
    | 'minCurrentSunMode'
    | 'fallbackMaxCurrent'
    | 'chargeInFallbackModeAllowed'
    | 'protection'
    | 'ledIntensity'
    | 'useMidMeter'
    | 'pulseWeight'
    | 'sunMode'
    | 'phaseMapping'
  >
  evseSubTypeParameters: Pick<ControlledDto['evseSubTypeParameters'], 'wallboxId' | 'wbType' | 'brand'> &
    Partial<Pick<ControlledDto['evseSubTypeParameters'], 'deviceReference' | 'keyLockMode'>>
}

export const createEvse = async (
  installationId: number,
  deviceId: string,
  evse: ControlledPOSTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<string | null>> => {
  try {
    const response = await authFetch<string>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controlled`,
      method: 'POST',
      payload: evse,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null, true)
    }

    return new ApiResult<string>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create EVSE:')
  }
}

export const pairModbusTcpEvse = async (
  installationId: number,
  emcDeviceId: string,
  modbusTcpDeviceId: string,
  macAddress: string,
  wbType: string | null,
  macOriginVerified: boolean,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcDeviceId}/sub/${modbusTcpDeviceId}/pair/evse`,
      method: 'POST',
      payload: {
        macAddress,
        offline: true,
        wbType,
        macOriginVerified,
      },
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to pair Modbus TCP EVSE:')
  }
}

export const pairEebusEvse = async (
  installationId: number,
  emcDeviceId: string,
  eebusDeviceId: string,
  ski: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcDeviceId}/sub/${eebusDeviceId}/pair/evse`,
      method: 'POST',
      payload: { ski },
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to pair EEBus EVSE:')
  }
}

export const updateModbusTcpEvseOcppPassword = async (
  installationId: number,
  emcDeviceId: string,
  modbusTcpDeviceId: string,
  password: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcDeviceId}/sub/${modbusTcpDeviceId}/ocpp/password`,
      method: 'PUT',
      payload: {
        password,
      },
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update Modbus TCP EVSE OCPP password:')
  }
}

export const deleteEvse = async (
  installationId: number,
  deviceId: string,
  evseId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controlled/${evseId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete EVSE:')
  }
}

export type MeterPOSTorPUTDto = Pick<MeterDto, 'deviceName' | 'media' | 'mediaParameters' | 'deviceType' | 'typeParameters' | 'wiringMode'> & {
  number?: number
}

export const createMeter = async (
  installationId: number,
  deviceId: string,
  meter: MeterPOSTorPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<string | null>> => {
  try {
    const response = await authFetch<string>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/monitoring/meters`,
      method: 'POST',
      payload: meter,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<string>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create meter:')
  }
}

export const updateMeter = async (
  installationId: number,
  deviceId: string,
  meterId: string,
  meter: MeterPOSTorPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/monitoring/meters/${meterId}`,
      method: 'PUT',
      payload: meter,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update meter:')
  }
}

export const deleteMeter = async (
  installationId: number,
  deviceId: string,
  meterId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/monitoring/meters/${meterId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete meter:')
  }
}

export const createHtc320hIo = async (
  installationId: number,
  deviceId: string,
  htc320h: Htc320hIo,
  treatHtcAsNonSgReady: boolean,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const { name, pairingId, isConsumptionActive, totalPowerConsumption, isProductionActive } = htc320h

    const nominalPower: number = treatHtcAsNonSgReady ? 0 : htc320h.nominalPower || 0

    const modbusRtu = {
      deviceName: sanitizeToLatin(name),
      type: 'IOController',
      media: 'ModbusRTU',
      mediaParameters: {
        address: Number(pairingId) || null,
      },
      inputs: [
        {
          type: 'consumption',
          index: 0,
          active: isConsumptionActive,
          value: isConsumptionActive ? totalPowerConsumption : 0,
        },
        {
          type: 'production',
          index: 1,
          active: isProductionActive,
          value: 0,
        },
      ],
      outputConfiguration: 'SGReady',
      outputs: [
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady1',
        },
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady2',
        },
      ],
    }

    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers`,
      method: 'POST',
      payload: modbusRtu,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create HTC320h IO:')
  }
}

export const updateHtc320hIo = async (
  installationId: number,
  deviceId: string,
  htc320hId: string,
  htc320h: Htc320hIo,
  treatHtcAsNonSgReady: boolean,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const { name, pairingId, isConsumptionActive, totalPowerConsumption, isProductionActive, rawData } = htc320h

    const hasAddressChanged = Number(pairingId) !== (rawData?.media === 'ModbusRTU' ? rawData?.mediaParameters?.address : null)

    const nominalPower: number = treatHtcAsNonSgReady ? 0 : htc320h.nominalPower || 0

    const modbusRtu = {
      deviceName: sanitizeToLatin(name),
      type: 'IOController',
      ...(hasAddressChanged
        ? {
            media: 'ModbusRTU',
            mediaParameters: {
              address: Number(pairingId) || null,
            },
          }
        : {}),
      inputs: [
        {
          type: 'consumption',
          index: 0,
          active: isConsumptionActive,
          value: isConsumptionActive ? totalPowerConsumption : 0,
        },
        {
          type: 'production',
          index: 1,
          active: isProductionActive,
          value: 0,
        },
      ],
      parameters: {
        controlMode: null,
        boostConfig: [],
        inputReadFreq: 2,
        nominalPower: null,
      },
      outputConfiguration: 'SGReady',
      outputs: [
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady1',
        },
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady2',
        },
      ],
    }

    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers/${htc320hId}`,
      method: 'PUT',
      payload: modbusRtu,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update HTC320h IO:')
  }
}

type StorageIoProps = {
  deviceName: string
  nominalPower: number
  mediaParameters?: { deviceLinkId: string }
}

export const createStorageIo = async (
  installationId: number,
  deviceId: string,
  storageIoProps: StorageIoProps,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const { deviceName, nominalPower, mediaParameters } = storageIoProps

    const storageIo = {
      deviceName: sanitizeToLatin(deviceName),
      media: 'Storage',
      mediaParameters: mediaParameters,
      type: 'IOController',
      inputs: [],
      outputConfiguration: 'SGReady',
      outputs: [
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady1',
        },
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady2',
        },
      ],
    }

    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers`,
      method: 'POST',
      payload: storageIo,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create storage IO:')
  }
}

export const updateStorageIo = async (
  installationId: number,
  deviceId: string,
  storageIoId: string,
  storageIoProps: StorageIoProps,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const { deviceName, nominalPower } = storageIoProps

    const storageIo = {
      deviceName: sanitizeToLatin(deviceName),
      type: 'IOController',
      parameters: {
        controlMode: null,
        boostConfig: [],
        inputReadFreq: 2,
        nominalPower: null,
      },
      inputs: [],
      outputConfiguration: 'SGReady',
      outputs: [
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady1',
        },
        {
          deviceType: 'HVAC',
          typeParameters: {
            nominalPower,
          },
          features: 'SGReady2',
        },
      ],
    }

    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers/${storageIoId}`,
      method: 'PUT',
      payload: storageIo,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update storage IO:')
  }
}

export const createMyPvIo = async (
  installationId: number,
  deviceId: string,
  myPv: MyPvIo,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const myPvPayload = getMyPvPOSTDto(myPv)

    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers`,
      method: 'POST',
      payload: myPvPayload,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create MyPV IO:')
  }
}

export const updateMyPvIo = async (
  installationId: number,
  deviceId: string,
  myPvId: string,
  payload: MyPvPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers/${myPvId}`,
      method: 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update MyPV IO:')
  }
}

export const createHeatpumpIo = async (
  installationId: number,
  deviceId: string,
  heatpump: HeatpumpIo,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const { name, nominalPower } = heatpump

    const heatpumpPayload = {
      deviceName: sanitizeToLatin(name),
      media: 'ModbusTCP',
      type: 'HpWithISG',
      parameters: {
        nominalPower: nominalPower || 1,
      },
    }

    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers`,
      method: 'POST',
      payload: heatpumpPayload,
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create heatpump IO:')
  }
}

export const updateHeatpumpIo = async (
  installationId: number,
  deviceId: string,
  heatpumpId: string,
  heatpump: HeatpumpIo,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const { name, nominalPower } = heatpump
    const payload = {
      deviceName: sanitizeToLatin(name),
      parameters: {
        nominalPower: nominalPower || 1,
      },
    }

    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers/${heatpumpId}`,
      method: 'PUT',
      payload,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update heatpump IO:')
  }
}

export const pairHeatpumpIo = async (
  installationId: number,
  emcDeviceId: string,
  heatpumpDeviceId: string,
  macAddress: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${emcDeviceId}/sub/${heatpumpDeviceId}/pair/hp-with-isg`,
      method: 'POST',
      payload: {
        macAddress,
      },
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to pair heatpump IO:')
  }
}

export const deleteIo = async (
  installationId: number,
  deviceId: string,
  ioId: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/device-links/${deviceId}/sub/controllers/${ioId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete IO:')
  }
}
